import React, { FC } from "react";

import { ContentBox, GridItem, GridLayout } from "ui-library/layouts";

import i18n from "core/i18n";
import {
    emailCharacters,
    lettersNumbersBasicInterpunctionCharacters,
    max10Characters,
    max20Characters,
    max50Characters,
    numberCharacters,
    phoneNumberCharacters,
} from "core/form";

import { Field } from "core/hookForm";
import { SubjectTypeEnum } from "types";
import { useFormContext } from "react-hook-form";

import { VehicleCancellationFormValues } from "../../types";

type SubjectFieldsProps = {
    sectionName: "contactPerson";
};

export const SubjectFields: FC<SubjectFieldsProps> = ({ sectionName }) => {
    const { t } = i18n.useTranslation();
    const { watch } = useFormContext<VehicleCancellationFormValues>();

    const selectedLegalPersonCode: SubjectTypeEnum = watch("contactPerson.subjectType");

    const subjectTypeOptions = [
        {
            code: SubjectTypeEnum.INDIVIDUAL,
            value: t("common.individual"),
        },
        {
            code: SubjectTypeEnum.COMPANY,
            value: t("common.legalEntity"),
        },
    ];

    return (
        <>
            <ContentBox fullWidth>
                <GridLayout spacing={2}>
                    <GridItem xs={12} md={6}>
                        <Field<VehicleCancellationFormValues>
                            name={`${sectionName}.subjectType`}
                            input="select"
                            label={t("common.legalPersonCode")}
                            options={subjectTypeOptions}
                        />
                    </GridItem>
                    <GridItem xs={12} md={6}>
                        {selectedLegalPersonCode === SubjectTypeEnum.INDIVIDUAL ? (
                            <Field<VehicleCancellationFormValues>
                                name={`${sectionName}.birthNumber`}
                                input="textField"
                                label={t("holder.birthNumber")}
                                normalize={[numberCharacters, max10Characters]}
                            />
                        ) : (
                            <Field<VehicleCancellationFormValues>
                                name={`${sectionName}.companyIdentificationNumber`}
                                input="textField"
                                label={t("holder.companyID")}
                                normalize={[numberCharacters, max10Characters]}
                            />
                        )}
                    </GridItem>
                </GridLayout>
            </ContentBox>

            {/* Person subject */}
            {selectedLegalPersonCode === SubjectTypeEnum.INDIVIDUAL && (
                <ContentBox fullWidth>
                    <GridLayout spacing={2}>
                        <GridItem xs={12} md={6}>
                            <Field<VehicleCancellationFormValues>
                                name={`${sectionName}.firstName`}
                                input="textField"
                                label={t("holder.firstName")}
                                normalize={[lettersNumbersBasicInterpunctionCharacters, max50Characters]}
                            />
                        </GridItem>
                        <GridItem xs={12} md={6}>
                            <Field<VehicleCancellationFormValues>
                                name={`${sectionName}.lastName`}
                                input="textField"
                                label={t("holder.lastName")}
                                normalize={[lettersNumbersBasicInterpunctionCharacters, max50Characters]}
                            />
                        </GridItem>
                    </GridLayout>
                </ContentBox>
            )}

            {/* Company subject */}
            {selectedLegalPersonCode === SubjectTypeEnum.COMPANY && (
                <ContentBox fullWidth>
                    <Field<VehicleCancellationFormValues>
                        name={`${sectionName}.companyName`}
                        input="textField"
                        label={t("holder.companyName")}
                        normalize={[lettersNumbersBasicInterpunctionCharacters, max50Characters]}
                    />
                </ContentBox>
            )}

            <ContentBox fullWidth>
                <GridLayout spacing={2}>
                    <GridItem xs={12} md={6}>
                        <Field<VehicleCancellationFormValues>
                            name={`${sectionName}.phoneNumber`}
                            input="textField"
                            label={t("holder.tel")}
                            normalize={[phoneNumberCharacters, max20Characters]}
                        />
                    </GridItem>
                    <GridItem xs={12} md={6}>
                        <Field<VehicleCancellationFormValues>
                            name={`${sectionName}.email`}
                            input="textField"
                            label={t("holder.emailAddress")}
                            normalize={[emailCharacters, max50Characters]}
                        />
                    </GridItem>
                </GridLayout>
            </ContentBox>
        </>
    );
};
